import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { AvailableTemplatesService } from '../available-templates.service';
import { AlertSettingsVariable } from 'src/app/hickory-api';
import { PlaceholderUtilities } from '../placeholder-utilities';
import { ErrorStateMatcher } from '@angular/material/core';
import { AlertModelService } from 'src/app/alert/shared/alert-model';

export interface TemplateVariable {
  id: string;
  name: string;
  description: string;
  sample?: string;
}

export class AlwaysErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl): boolean {
    return control && control.invalid;
  }
}

@Component({
  selector: 'app-templated-simple-input',
  templateUrl: './templated-simple-input.component.html',
  styleUrl: './templated-simple-input.component.scss',
  standalone: false
})
export class TemplatedSimpleInputComponent implements AfterViewInit {
  inputControl = new FormControl('');

  allVariables: AlertSettingsVariable[] = [];
  filteredVariables: AlertSettingsVariable[] = [];

  displayChips = true;
  useSample = false;
  previewInput = false;

  get showPreview() {
    return this.inputHasVariables && this.inputControl?.valid && this.previewInput;
  }

  @Input() label: string = 'Subject';

  @Input()
  get disabled() {
    return this.inputControl?.disabled;
  }
  set disabled(value: boolean) {
    if (value) {
      this.inputControl?.disable();
    }
  }

  @Input()
  get required() {
    return this.inputControl?.hasValidator(Validators.required);
  }
  set required(value: boolean) {
    if (value) {
      this.inputControl?.addValidators(Validators.required);
    }
  }

  @Input()
  get value() {
    return this.inputControl?.value ?? '';
  }
  set value(value: string) {
    this.inputControl?.setValue(value);
    this.inputControl?.updateValueAndValidity();
  }

  @Input() readonly: boolean = false;
  @Input() readonlyText: string = 'Readonly';
  @Input() showReset: boolean = false;

  @Input() showHint: boolean = false;
  @Input() hintClass: string = '';
  @Input() hintText: string = '';

  @Input() useIntegratedPreview: boolean = true;
  @Input() getAllTemplates: boolean = false;

  @Output() resetClicked = new EventEmitter<void>();
  @Output() valueChange = new EventEmitter<string>();
  @Output() modelChange = new EventEmitter<string>();

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('inputEl') inputEl!: ElementRef<HTMLInputElement>;

  trigger = '{{';
  private currentTriggerPosition = -1;
  private originalInputValue = '';
  private originalCursorPosition = 0;
  private isUserInteraction = false;

  errorMatcher = new AlwaysErrorStateMatcher();

  get inputHasVariables() {
    if (typeof this.inputControl.value === 'string') {
      return PlaceholderUtilities.extractPlaceholders(this.inputControl.value).length != 0;
    }
    return false;
  }

  get previewHtml(): string {
    if (typeof this.inputControl.value === 'string') {
      return this.inputControl.value.replace(/{{\s*([^{}]+?)\s*}}/g, (_match, variableName: string) => {
        const trimmedName = variableName.trim();
        const foundVar = this.allVariables.find((item) => {
          return trimmedName == item.placeHolder;
        });

        if (!foundVar) {
          return variableName;
        }

        return `<span class="${this.displayChips ? 'chip-style' : ''}">${this.useSample ? foundVar.sampleValue : foundVar.friendlyName}</span>`;
      });
    }

    return '';
  }

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private readonly variablesService: AvailableTemplatesService,
    private alertModel: AlertModelService
  ) {}

  ngAfterViewInit() {
    if (this.getAllTemplates) {
      this.variablesService.getAllAvailableTemplates(this.alertModel?.alertTemplate?.id).subscribe((result) => {
        this.allVariables = result.items;
        this.inputControl.addValidators([this.unclosedVariableValidator(), this.variableExistsValidator()]);
        this.inputControl.updateValueAndValidity();
      });

    } else {
      this.variablesService.getAllAvailableAlertTemplates(this.alertModel?.alertTemplate?.id).subscribe((result) => {
        this.allVariables = result.items;
        this.inputControl.addValidators([this.unclosedVariableValidator(), this.variableExistsValidator()]);
        this.inputControl.updateValueAndValidity();
      });
    }

    this.inputEl.nativeElement.addEventListener('input', () => {
      this.isUserInteraction = true;
    })

    this.inputControl.valueChanges.subscribe((value) => {
      // Ensure we're always working with a string
      const stringValue = typeof value === 'string' ? value : '';
      const pos = this.inputEl.nativeElement.selectionStart;

      const context = this.getTriggerContext(stringValue, pos);

      if (context && this.isUserInteraction) {
        this.currentTriggerPosition = context.start;
        this.originalInputValue = stringValue;
        this.originalCursorPosition = pos;
        this.filteredVariables = this.filterVariables(context.query);
        this.autocompleteTrigger.openPanel();
      } else {
        this.filteredVariables = [];
        this.autocompleteTrigger.closePanel();
      }

      if (typeof value === 'string') {
        this.valueChange.emit(value);
        if (this.inputControl.dirty) {
          this.modelChange.emit(value);
        }
      }
    });
  }

  unclosedVariableValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null; // Ignore empty values
      if (typeof control.value !== 'string') return null;

      const text: string = control.value;
      const openCount = (text.match(/{{/g) || []).length;
      const closeCount = (text.match(/}}/g) || []).length;

      if (openCount > closeCount) {
        return {
          unclosedBraces: {
            valid: false,
            message: 'Unclosed double curly braces detected.',
          },
        };
      }

      return null; // No errors
    };
  }

  onResetClick() {
    this.inputControl.markAsPristine();
    this.resetClicked.emit();
  }

  variableExistsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null; // Ignore empty values
      if (typeof control.value !== 'string') return null;

      const text: string = control.value;
      const templateValues = PlaceholderUtilities.extractPlaceholders(text);

      for (const value of templateValues) {
        const found = this.allVariables.some((variable) => variable.placeHolder === value);

        if (!found) {
          return {
            variableExists: {
              valid: false,
              message: `"${value}" is not a valid placeholder.`,
            },
          };
        }
      }

      return null; // Everything is valid
    };
  }

  private getTriggerContext(text: string, cursorPos: number | null): { start: number; query: string } | null {
    if (!text || cursorPos === null) return null;

    // Explicit type check
    if (typeof text !== 'string') return null;

    // Find the last unclosed trigger before cursor
    const textBeforeCursor = text.slice(0, cursorPos);
    const lastTriggerIndex = textBeforeCursor.lastIndexOf(this.trigger);

    if (lastTriggerIndex === -1) return null;

    // Check if trigger is closed
    const textAfterTrigger = text.slice(lastTriggerIndex);
    if (textAfterTrigger.includes('}}')) return null;

    const queryStart = lastTriggerIndex + this.trigger.length;
    const query = text.slice(queryStart, cursorPos).trim();

    return { start: lastTriggerIndex, query };
  }

  private filterVariables(query: string): AlertSettingsVariable[] {
    return this.allVariables.filter(
      (v) => v.friendlyName.toLowerCase().includes(query.toLowerCase())
    );
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    // Get the selected variable object from the option's value
    const variable = event.option.value as AlertSettingsVariable;

    // Use the original input state before autocomplete triggered
    const newValue =
      this.originalInputValue.slice(0, this.currentTriggerPosition) +
      `{{ ${variable.placeHolder} }}` +
      this.originalInputValue.slice(this.originalCursorPosition);

    // Update the form control value
    this.inputControl.setValue(newValue);

    // Set cursor position after inserted variable
    const newCursorPos = this.currentTriggerPosition + `{{ ${variable.placeHolder} }}`.length;
    setTimeout(() => {
      this.inputEl.nativeElement.setSelectionRange(newCursorPos, newCursorPos);
    });
  }
}
